import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdminLayout from './layouts/AdminLayout';
import {
  ArrowLeft,
  Building,
  MapPin,
  Calendar,
  DollarSign,
  Clock,
  Users,
  Eye,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Briefcase,
  Star,
  Globe,
  Phone,
  Mail,
  Printer,
  ExternalLink,
  Download,
  RefreshCw,
  FileText,
  Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const StatusBadge = ({ status, isApproved }) => {
  if (!isApproved) {
    return (
      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
        <Clock className="w-4 h-4 mr-1.5" />
        Pending Approval
      </span>
    );
  }
  
  return status ? (
    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
      <CheckCircle className="w-4 h-4 mr-1.5" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-200">
      <XCircle className="w-4 h-4 mr-1.5" />
      Inactive
    </span>
  );
};

const InfoCard = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${className}`}>
    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
        <Icon className="w-5 h-5 mr-2 text-gray-600" />
        {title}
      </h3>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

const DataField = ({ label, value, icon: Icon, className = "" }) => {
  if (!value) return null;
  
  return (
    <div className={`flex items-start space-x-3 ${className}`}>
      {Icon && <Icon className="w-5 h-5 mt-0.5 text-gray-500 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="text-base text-gray-900 break-words">{value}</p>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
    red: "bg-red-50 text-red-700 border-red-200"
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center">
        <Icon className="w-8 h-8 mr-3" />
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm font-medium">{title}</p>
        </div>
      </div>
    </div>
  );
};

const AdminJobView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchJobDetails();
    fetchApplications();
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/jobs/${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setJob(data);
    } catch (err) {
      setError("Failed to fetch job details. Please try again.");
      console.error("Error fetching job:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await fetch(`${API_URL}/applications?jobId=${id}`);
      
      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || data || []);
      }
    } catch (err) {
      console.error("Error fetching applications:", err);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm("Are you sure you want to approve this job listing?")) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(`${API_URL}/jobs/${id}/approve`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const updatedJob = await response.json();
      setJob(updatedJob);
    } catch (err) {
      setError("Failed to approve job listing");
      console.error("Approve error:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = !job.isActive;
    const action = newStatus ? "activate" : "deactivate";
    
    if (!window.confirm(`Are you sure you want to ${action} this job listing?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(`${API_URL}/jobs/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: newStatus }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const updatedJob = await response.json();
      setJob(updatedJob);
    } catch (err) {
      setError("Failed to update job status");
      console.error("Toggle status error:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this job listing? This action cannot be undone.")) {
      return;
    }
    
    try {
      setActionLoading(true);
      const response = await fetch(`${API_URL}/jobs/${id}`, {
        method: "DELETE",
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      navigate('/admin/job-listings');
    } catch (err) {
      setError("Failed to delete job listing");
      console.error("Delete error:", err);
      setActionLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !job) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center py-12">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              {error || "Job not found"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              The job listing could not be loaded. Please try again or contact support.
            </p>
            <div className="mt-6">
              <Link
                to="/admin/job-listings"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Job Listings
              </Link>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const stats = {
    applications: applications.length,
    views: job.views || 0,
    daysLive: Math.floor((new Date() - new Date(job.postedDate)) / (1000 * 60 * 60 * 24))
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>Admin | {job.title} - Job Details</title>
      </Helmet>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print\\:text-black { color: black !important; }
          body { background: white !important; }
          .shadow-sm, .shadow-md, .shadow-lg { box-shadow: none !important; }
        }
      `}</style>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6 print:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/admin/job-listings"
                className="inline-flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                Back to Job Listings
              </Link>
              <div className="h-6 border-l border-gray-300"></div>
              <StatusBadge status={job.isActive} isApproved={job.isApproved} />
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handlePrint}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </button>
              
              {!job.isApproved && (
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {actionLoading ? 'Approving...' : 'Approve'}
                </button>
              )}
              
              <button
                onClick={handleToggleStatus}
                disabled={actionLoading}
                className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                  job.isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                } disabled:opacity-50`}
              >
                {job.isActive ? <XCircle className="w-4 h-4 mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                {actionLoading ? 'Processing...' : (job.isActive ? 'Deactivate' : 'Activate')}
              </button>
              
              <Link
                to={`/edit-job/${id}`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Link>
              
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Job Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Header */}
            <InfoCard title="Job Information" icon={Briefcase}>
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{job.title}</h1>
                  <div className="flex flex-wrap items-center gap-4 text-gray-600">
                    <div className="flex items-center">
                      <Building className="w-5 h-5 mr-2" />
                      <span className="font-medium">{job.company}</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="w-5 h-5 mr-2" />
                      <span>{job.location}</span>
                    </div>
                    {job.salary && (
                      <div className="flex items-center">
                        <DollarSign className="w-5 h-5 mr-2" />
                        <span>{job.salary}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DataField
                    label="Posted Date"
                    value={new Date(job.postedDate).toLocaleDateString()}
                    icon={Calendar}
                  />
                  <DataField
                    label="Job Type"
                    value={job.jobType || "Not specified"}
                    icon={Briefcase}
                  />
                  <DataField
                    label="Experience Level"
                    value={job.experienceLevel || "Not specified"}
                    icon={Star}
                  />
                  {job.deadline && (
                    <DataField
                      label="Application Deadline"
                      value={new Date(job.deadline).toLocaleDateString()}
                      icon={Clock}
                    />
                  )}
                </div>
              </div>
            </InfoCard>

            {/* Job Description */}
            <InfoCard title="Job Description" icon={FileText}>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                  {job.description}
                </p>
              </div>
            </InfoCard>

            {/* Requirements */}
            {job.requirements && job.requirements.length > 0 && (
              <InfoCard title="Requirements" icon={Award}>
                <div className="space-y-2">
                  {Array.isArray(job.requirements) ? (
                    <ul className="list-disc list-inside space-y-1">
                      {job.requirements.map((req, index) => (
                        <li key={index} className="text-gray-700">{req}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-700 whitespace-pre-line">{job.requirements}</p>
                  )}
                </div>
              </InfoCard>
            )}

            {/* Skills */}
            {job.requiredSkills && job.requiredSkills.length > 0 && (
              <InfoCard title="Required Skills" icon={Star}>
                <div className="flex flex-wrap gap-2">
                  {job.requiredSkills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </InfoCard>
            )}
          </div>

          {/* Right Column - Statistics and Actions */}
          <div className="space-y-6">
            {/* Statistics */}
            <div className="space-y-4">
              <StatCard
                title="Applications"
                value={stats.applications}
                icon={Users}
                color="blue"
              />
              <StatCard
                title="Views"
                value={stats.views}
                icon={Eye}
                color="green"
              />
              <StatCard
                title="Days Live"
                value={stats.daysLive}
                icon={Calendar}
                color="yellow"
              />
            </div>

            {/* Quick Actions */}
            <InfoCard title="Quick Actions" icon={RefreshCw} className="print:hidden">
              <div className="space-y-3">
                <Link
                  to={`/admin/applications?jobId=${id}`}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Users className="w-4 h-4 mr-2" />
                  View Applications ({stats.applications})
                </Link>
                
                <a
                  href={`/job/${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Public Page
                </a>

                <button
                  onClick={fetchJobDetails}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Data
                </button>
              </div>
            </InfoCard>

            {/* Job Status Info */}
            <InfoCard title="Status Information" icon={AlertTriangle}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Approval Status</span>
                  <span className={`text-sm font-medium ${job.isApproved ? 'text-green-600' : 'text-yellow-600'}`}>
                    {job.isApproved ? 'Approved' : 'Pending Approval'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Active Status</span>
                  <span className={`text-sm font-medium ${job.isActive ? 'text-green-600' : 'text-red-600'}`}>
                    {job.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Featured</span>
                  <span className={`text-sm font-medium ${job.isFeatured ? 'text-green-600' : 'text-gray-600'}`}>
                    {job.isFeatured ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </InfoCard>
          </div>
        </div>

        {/* Recent Applications Preview */}
        {applications.length > 0 && (
          <div className="mt-8 print:hidden">
            <InfoCard title="Recent Applications" icon={Users}>
              <div className="space-y-4">
                {applications.slice(0, 5).map((application) => (
                  <div
                    key={application._id}
                    className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {application.firstName} {application.lastName}
                      </p>
                      <p className="text-sm text-gray-500">{application.email}</p>
                      <p className="text-xs text-gray-400">
                        Applied {new Date(application.appliedDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        application.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                        application.status === 'Reviewed' ? 'bg-blue-100 text-blue-800' :
                        application.status === 'Interviewed' ? 'bg-purple-100 text-purple-800' :
                        application.status === 'Offered' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {application.status || 'Pending'}
                      </span>
                      <Link
                        to={`/admin/applications/${application._id}`}
                        className="text-primary hover:text-primary-dark"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                ))}
                
                {applications.length > 5 && (
                  <div className="pt-3">
                    <Link
                      to={`/admin/applications?jobId=${id}`}
                      className="text-sm text-primary hover:text-primary-dark font-medium"
                    >
                      View all {applications.length} applications →
                    </Link>
                  </div>
                )}
              </div>
            </InfoCard>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminJobView; 